import { createSelector } from '@reduxjs/toolkit'
import type { RootState } from '@/shared/store'
import type { LocalLayer } from '../layer/types'
import { selectAllCurrentLayers, selectAllOriginalLayers } from '../layer/layerSelectors'
import { selectAllSlides } from '../slide/slideSelectors'

const selectSyncState = (state: RootState) => state.editor.sync

/** IDs of slides that have unsaved changes */
export const selectModifiedSlideIds = createSelector(
  [selectAllSlides, selectAllCurrentLayers, selectAllOriginalLayers],
  (slides, currentLayers, originalLayers): number[] =>
    slides
      .filter((slide) => {
        if (slide.deletedLayerIds.length > 0) return true

        const current = currentLayers.filter((l) => l.slideId === slide.id)
        const original = originalLayers.filter((l) => l.slideId === slide.id)

        return JSON.stringify(current) !== JSON.stringify(original)
      })
      .map((s) => s.id),
)

/** Whether any slides have unsaved changes */
export const selectHasUnsavedChanges = createSelector(
  [selectModifiedSlideIds],
  (modifiedIds): boolean => modifiedIds.length > 0,
)

/** Whether a specific slide has unsaved changes */
export const selectSlideHasChanges = createSelector(
  [
    selectAllSlides,
    selectAllCurrentLayers,
    selectAllOriginalLayers,
    (_state: RootState, slideId: number) => slideId,
  ],
  (slides, currentLayers, originalLayers, slideId): boolean => {
    const slide = slides.find((s) => s.id === slideId)

    if (!slide) return false
    if (slide.deletedLayerIds.length > 0) return true

    const current = currentLayers.filter((l) => l.slideId === slideId)
    const original = originalLayers.filter((l) => l.slideId === slideId)

    return JSON.stringify(current) !== JSON.stringify(original)
  },
)

/** Map of slideId → text layers */
export const selectAllTextLayersMap = createSelector(
  [selectAllSlides, selectAllCurrentLayers],
  (slides, layers): Map<number, LocalLayer[]> => {
    const map = new Map<number, LocalLayer[]>()

    for (const slide of slides) {
      const textLayers = layers
        .filter((l) => l.slideId === slide.id && l.layer_type === 'text')
        .sort((a, b) => (a.z_index ?? 0) - (b.z_index ?? 0))

      map.set(slide.id, textLayers)
    }

    return map
  },
)

/** Map of slideId → non-background image layers */
export const selectAllImageLayersMap = createSelector(
  [selectAllSlides, selectAllCurrentLayers],
  (slides, layers): Map<number, LocalLayer[]> => {
    const map = new Map<number, LocalLayer[]>()

    for (const slide of slides) {
      const imageLayers = layers
        .filter((l) => l.slideId === slide.id && l.layer_type === 'image' && l.image_asset_type !== 'background')
        .sort((a, b) => (a.z_index ?? 0) - (b.z_index ?? 0))

      map.set(slide.id, imageLayers)
    }

    return map
  },
)

/** Map of slideId → all non-background layers */
export const selectAllNonBgLayersMap = createSelector(
  [selectAllSlides, selectAllCurrentLayers],
  (slides, layers): Map<number, LocalLayer[]> => {
    const map = new Map<number, LocalLayer[]>()

    for (const slide of slides) {
      const nonBgLayers = layers
        .filter((l) => l.slideId === slide.id && !(l.layer_type === 'image' && l.image_asset_type === 'background'))
        .sort((a, b) => (a.z_index ?? 0) - (b.z_index ?? 0))

      map.set(slide.id, nonBgLayers)
    }

    return map
  },
)

/** Map of slideId → background layer or null */
export const selectBackgroundLayerMap = createSelector(
  [selectAllSlides, selectAllCurrentLayers],
  (slides, layers): Map<number, LocalLayer | null> => {
    const map = new Map<number, LocalLayer | null>()

    for (const slide of slides) {
      const bg = layers.find(
        (l) => l.slideId === slide.id && l.layer_type === 'image' && l.image_asset_type === 'background',
      ) ?? null

      map.set(slide.id, bg)
    }

    return map
  },
)

/** Map of slideId → all layers including background */
export const selectFullLayersMap = createSelector(
  [selectAllSlides, selectAllCurrentLayers],
  (slides, layers): Map<number, LocalLayer[]> => {
    const map = new Map<number, LocalLayer[]>()

    for (const slide of slides) {
      const slideLayers = layers
        .filter((l) => l.slideId === slide.id)
        .sort((a, b) => (a.z_index ?? 0) - (b.z_index ?? 0))

      map.set(slide.id, slideLayers)
    }

    return map
  },
)

/** Sync status metadata */
export const selectSyncStatus = createSelector(
  [selectSyncState],
  (state) => ({
    isSyncing: state.isSyncing,
    syncError: state.syncError,
    lastSyncedAt: state.lastSyncedAt,
  }),
)

/** Current story ID */
export const selectStoryId = createSelector(
  [selectSyncState],
  (state) => state.storyId,
)

/** Current story title */
export const selectStoryTitle = createSelector(
  [selectSyncState],
  (state) => state.storyTitle,
)
